/**
 * Copyright (c) 2025 Aisle 24. All rights reserved.
 *
 * Proprietary Software - Licensed for Internal Use Only
 *
 * This software and associated documentation files (the "Software") are proprietary
 * to Aisle 24 and are protected by copyright laws and international copyright
 * treaties, as well as other intellectual property laws and treaties.
 *
 * The Software is licensed, not sold, to you for use only under the terms of this
 * license agreement. All rights not expressly granted to you are reserved by Aisle 24.
 *
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium is strictly prohibited and may result in severe civil and
 * criminal penalties, and will be prosecuted to the maximum extent possible
 * under the law.
 */

// Whitelabel Branding Configuration
// Supports multiple tenants: Aisle 24, Ontario Tech, etc.

export type TenantId = 'aisle24' | 'ontariotech';

export interface TenantBranding {
  id: TenantId;
  name: string;
  displayName: string;
  shortName: string;
  description: string;
  copyright: string;
  
  // Brand Colors
  colors: {
    primary: string;
    secondary: string;
    primaryDark: string;
    primaryLight: string;
    secondaryDark: string;
    secondaryLight: string;
  };
  
  // Database Configuration
  database: {
    projectId: string;
    instanceId: string;
    databaseId: string;
    region: string;
  };
  
  // Receipt Branding
  receipt: {
    title: string;
    footerMessage: string;
    logo?: string;
  };
  
  // Image Paths
  images: {
    logo: string;
    bookPlaceholder: string;
    basePath: string;
  };
  
  // Store Information
  store: {
    name: string;
    address?: {
      street: string;
      city: string;
      province: string;
      postalCode: string;
    };
  };
  
  // Deployment Server Configuration
  deployment: {
    serverIp: string;
    serverPort: number;
  };
}

// Tenant Branding Definitions
export const TENANT_BRANDING: Record<TenantId, TenantBranding> = {
  aisle24: {
    id: 'aisle24',
    name: 'Aisle 24',
    displayName: 'Aisle 24 POS',
    shortName: 'Aisle 24',
    description: 'Cashier-less POS system for Aisle 24',
    copyright: '© 2025 Aisle 24. All rights reserved.',
    colors: {
      primary: '#2C507A',
      secondary: '#F06C38',
      primaryDark: '#1E3A5F',
      primaryLight: '#4A6B8A',
      secondaryDark: '#D55A2B',
      secondaryLight: '#F28B5C',
    },
    database: {
      projectId: process.env.REACT_APP_AISLE24_PROJECT_ID || 'aisle24-pos',
      instanceId: process.env.REACT_APP_AISLE24_INSTANCE_ID || 'aisle24-instance',
      databaseId: process.env.REACT_APP_AISLE24_DATABASE_ID || 'aisle24-db',
      region: process.env.REACT_APP_AISLE24_REGION || 'us-central1',
    },
    receipt: {
      title: 'Aisle 24',
      footerMessage: 'Thank you for shopping at Aisle 24!',
    },
    store: {
      name: 'Aisle 24',
    },
    images: {
      logo: '/images/aisle24/Aisle 24 Logo.png',
      bookPlaceholder: '/images/shared/book-placeholder.svg',
      basePath: '/images/aisle24',
    },
    deployment: {
      serverIp: process.env.REACT_APP_DEPLOY_SERVER_IP || '143.244.128.192',
      serverPort: parseInt(process.env.REACT_APP_DEPLOY_SERVER_PORT || '8082', 10),
    },
  },
  
  ontariotech: {
    id: 'ontariotech',
    name: 'Ontario Tech University',
    displayName: 'Ontario Tech Bookstore POS',
    shortName: 'OT Bookstore POS',
    description: 'Cashier-less POS system for Ontario Tech University Bookstore',
    copyright: '© 2025 Ontario Tech University. All rights reserved.',
    colors: {
      primary: '#2C507A', // Ontario Tech dark blue
      secondary: '#F06C38', // Ontario Tech orange
      primaryDark: '#1E3A5F',
      primaryLight: '#4A6B8A',
      secondaryDark: '#D55A2B',
      secondaryLight: '#F28B5C',
    },
    database: {
      projectId: process.env.REACT_APP_ONTARIOTECH_PROJECT_ID || 'ontariotech-pos',
      instanceId: process.env.REACT_APP_ONTARIOTECH_INSTANCE_ID || 'ontariotech-instance',
      databaseId: process.env.REACT_APP_ONTARIOTECH_DATABASE_ID || 'ontariotech-db',
      region: process.env.REACT_APP_ONTARIOTECH_REGION || 'us-central1',
    },
    receipt: {
      title: 'Ontario Tech University Bookstore',
      footerMessage: 'Thank you for shopping at Ontario Tech Bookstore!',
    },
    store: {
      name: 'Ontario Tech University Bookstore',
      address: {
        street: '2000 Simcoe Street North',
        city: 'Oshawa',
        province: 'ON',
        postalCode: 'L1G 0C5',
      },
    },
    images: {
      logo: '/images/ontariotech/OntarioTech Logo.png',
      bookPlaceholder: '/images/shared/book-placeholder.svg',
      basePath: '/images/ontariotech',
    },
    deployment: {
      serverIp: process.env.REACT_APP_DEPLOY_SERVER_IP || '143.244.128.192',
      serverPort: parseInt(process.env.REACT_APP_DEPLOY_SERVER_PORT || '8082', 10),
    },
  },
};

// Get current tenant from environment variable or default
export const getCurrentTenant = (): TenantId => {
  const tenant = process.env.REACT_APP_TENANT_ID as TenantId;
  if (tenant && tenant in TENANT_BRANDING) {
    return tenant;
  }
  // Default to ontariotech (original development theme)
  return 'aisle24';
};

// Get branding configuration for current tenant
export const getCurrentBranding = (): TenantBranding => {
  const tenantId = getCurrentTenant();
  return TENANT_BRANDING[tenantId];
};

// Export current tenant branding as default
export const CURRENT_BRANDING = getCurrentBranding();

